/**
 * Fatiado de pages/ImportMenu.tsx por responsabilidade (#131, R3).
 */
import { useState } from 'react'
import { KickButton } from './ProcessingCard.js'
import { ReviewPanel } from './ReviewPanel.js'
import { importsApi } from '../../lib/api.js'
import { AwaitingCard } from './AwaitingCard.js'
import { ProcessingCard } from './ProcessingCard.js'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { MenuImportView } from '../../lib/api.js'
import { useI18n } from '../../lib/i18n.js'
import { cn } from '../../lib/utils.js'
import { Card, toast } from '../../components/ui/index.js'

// ─── Outcome: cardápio sendo montado, ao vivo (parte B) ──────────────────────

type FlagAction = 'keep' | 'switch'

/** Ícone de documento/página (lucide FileText, SVG inline — o admin não usa lib de ícones). */
function DocPageIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5 shrink-0"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  )
}

/** Ícone de troca (lucide ArrowLeftRight, SVG inline) — «mudar para a sugestão». */
function SwapIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5 shrink-0"
    >
      <path d="m8 3-4 4 4 4" />
      <path d="M4 7h16" />
      <path d="m16 21 4-4-4-4" />
      <path d="M20 17H4" />
    </svg>
  )
}

export function OutcomePanel({
  view,
  onCancelled,
}: {
  view: MenuImportView
  onCancelled?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const qc = useQueryClient()
  const draft = view.draft
  const [tab, setTab] = useState<'draft' | 'review'>('draft')
  // decisão EM CURSO por aviso (#403 spec final): dá estado real aos botões
  // (aria-pressed + fundo sólido) até a gravação terminar
  const [decided, setDecided] = useState<{
    all: FlagAction | null
    byId: Record<string, FlagAction>
  }>({ all: null, byId: {} })

  // decisão de aviso em 1 clique (keep = manter impresso / switch = aceitar a
  // sugestão da IA), individual ou em lote
  const flagMut = useMutation({
    mutationFn: (opts: { flagId?: string; all?: boolean; action: 'keep' | 'accept' }) =>
      opts.all
        ? importsApi.resolveAllFlags(view.id, opts.action)
        : importsApi.resolveFlag(view.id, opts.flagId!, opts.action),
    onSuccess: (data) => {
      toast.success(t('admin.import.flag_resolved_toast', { n: String(data.resolved) }))
      void qc.invalidateQueries({ queryKey: ['menu-import', view.id] })
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => setDecided({ all: null, byId: {} }),
  })
  const decide = (opts: { flagId?: string; all?: boolean; action: 'keep' | 'accept' }) => {
    // API aceita 'accept'; na UI a ação se chama «mudar para a sugestão»
    setDecided(
      opts.all
        ? { all: opts.action === 'keep' ? 'keep' : 'switch', byId: {} }
        : {
            all: null,
            byId: { ...decided.byId, [opts.flagId!]: opts.action === 'keep' ? 'keep' : 'switch' },
          },
    )
    flagMut.mutate(opts)
  }

  if (view.status === 'processing') {
    // #300: esperando o dono → a pergunta em destaque (nunca "sem progresso")
    return view.awaiting !== 'none' ? (
      <AwaitingCard view={view} onCancelled={onCancelled} />
    ) : (
      <ProcessingCard view={view} />
    )
  }

  if (!draft) return null

  const flags = draft.flags ?? []

  if (view.status === 'reviewing') {
    // progresso ao vivo contado do PRÓPRIO rascunho (persistido a cada lote):
    // aplicados = itens já corrigidos pela IA; restantes = itens com ajuste
    // ainda na fila (a corrente processa em lotes de 8, um LLM por lote)
    const applied = draft.categories.reduce(
      (sum, c) => sum + c.products.filter((p) => p.fixed).length,
      0,
    )
    const queued = draft.categories.reduce(
      (sum, c) => sum + c.products.filter((p) => p.review === 'fix' && !p.fixed).length,
      0,
    )
    const total = applied + queued
    const pct = total > 0 ? Math.round((applied / total) * 100) : 0
    return (
      <Card className="space-y-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
            {t('admin.import.phase_reviewing')}
          </p>
          <KickButton
            view={view}
            staleSec={Math.max(
              0,
              Math.round(Date.now() / 1000 - (view.updated_at ?? view.created_at)),
            )}
          />
        </div>
        {total > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                {t('admin.import.fix_progress', {
                  done: String(applied),
                  total: String(total),
                })}
              </span>
              <span className="tabular-nums">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full bg-orange-400 transition-all duration-500"
                style={{ width: `${Math.max(pct, 6)}%` }}
              />
            </div>
          </div>
        )}
        <p className="text-xs text-gray-400">{t('admin.import.outcome_hint_live')}</p>
      </Card>
    )
  }
  const pending = draft.categories.reduce(
    (sum, c) => sum + c.products.filter((p) => (p.review ?? 'pending') === 'pending').length,
    0,
  )

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        <TabButton
          testId="import-tab-draft"
          active={tab === 'draft'}
          onClick={() => setTab('draft')}
        >
          🍽 {t('admin.import.tab_draft')}
          {flags.length > 0 && (
            <span className="ml-1 rounded-full bg-amber-100 text-amber-800 px-1.5 text-[11px]">
              {flags.length}⚠
            </span>
          )}
        </TabButton>
        <TabButton
          testId="import-tab-review"
          active={tab === 'review'}
          onClick={() => setTab('review')}
        >
          ✎ {t('admin.import.tab_review')}
          {pending > 0 && (
            <span className="ml-1 rounded-full bg-gray-200 text-gray-700 px-1.5 text-[11px]">
              {pending}
            </span>
          )}
        </TabButton>
      </div>

      {tab === 'review' ? (
        <ReviewPanel view={view} />
      ) : (
        <Card className="space-y-3 max-h-[70vh] overflow-y-auto">
          {flags.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-bold text-amber-900">
                  {t('admin.import.flags_title', { n: String(flags.length) })}
                </p>
                {/* #403 spec final: MESMA ordem do bloco por item (manter
                    impresso primeiro, mudar para a sugestão depois); texto
                    sempre presente, ícone secundário; alvo ≥ 44px; anel de
                    foco saffron */}
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    disabled={flagMut.isPending}
                    aria-pressed={decided.all === 'keep'}
                    data-testid="import-flags-keep-all"
                    onClick={() => decide({ all: true, action: 'keep' })}
                    className={cn(
                      'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full border px-3 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pratzu-saffron focus-visible:ring-offset-2',
                      decided.all === 'keep'
                        ? 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'border-amber-300 bg-white text-amber-800 hover:bg-amber-100',
                    )}
                  >
                    <DocPageIcon />
                    {t('admin.import.flags_keep_all_printed')}
                  </button>
                  <button
                    type="button"
                    disabled={flagMut.isPending}
                    aria-pressed={decided.all === 'switch'}
                    data-testid="import-flags-switch-all"
                    onClick={() => decide({ all: true, action: 'accept' })}
                    className={cn(
                      'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full border px-3 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pratzu-saffron focus-visible:ring-offset-2',
                      decided.all === 'switch'
                        ? 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'border-amber-300 bg-white text-amber-800 hover:bg-amber-100',
                    )}
                  >
                    <SwapIcon />
                    {t('admin.import.flags_switch_all')}
                  </button>
                </div>
              </div>
              {flags.map((f) => (
                <div
                  key={f.id}
                  data-testid={`import-flag-${f.id}`}
                  className="rounded-lg border border-amber-200 bg-white p-2.5 space-y-1.5"
                >
                  <p className="text-xs text-amber-900">
                    ⚠ <span className="font-semibold">{f.product}</span> — {f.reason}
                  </p>
                  {/* #403 spec final: sem visto e sem X errado (as duas
                      opções são aceitações) — documento = manter como
                      impresso, setas de troca = mudar para a sugestão;
                      MESMA ordem do bloco em lote (manter primeiro, mudar
                      depois); fundo SÓLIDO na opção escolhida (aria-pressed)
                      até a gravação */}
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      disabled={flagMut.isPending}
                      aria-pressed={decided.byId[f.id] === 'keep'}
                      data-testid={`import-flag-keep-${f.id}`}
                      onClick={() => decide({ flagId: f.id, action: 'keep' })}
                      className={cn(
                        'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full border px-3 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pratzu-saffron focus-visible:ring-offset-2',
                        decided.byId[f.id] === 'keep'
                          ? 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700'
                          : 'border-amber-300 bg-white text-amber-800 hover:bg-amber-50',
                      )}
                    >
                      <DocPageIcon />
                      {t('admin.import.flag_keep_printed')}
                    </button>
                    <button
                      type="button"
                      disabled={flagMut.isPending}
                      aria-pressed={decided.byId[f.id] === 'switch'}
                      data-testid={`import-flag-switch-${f.id}`}
                      onClick={() => decide({ flagId: f.id, action: 'accept' })}
                      className={cn(
                        'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full border px-3 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pratzu-saffron focus-visible:ring-offset-2',
                        decided.byId[f.id] === 'switch'
                          ? 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700'
                          : 'border-amber-300 bg-white text-amber-800 hover:bg-amber-50',
                      )}
                    >
                      <SwapIcon />
                      {t('admin.import.flag_switch')}
                    </button>
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-amber-700">{t('admin.import.flags_hint')}</p>
            </div>
          )}

          {draft.categories.map((c) => (
            <div key={c.name} className="space-y-1">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                {c.name}
                <span className="ml-1 text-gray-300 font-normal">({c.products.length})</span>
              </p>
              {c.products.map((p, pi) => (
                <div
                  key={`${p.name}-${pi}`}
                  className="flex items-center gap-2 text-sm border border-gray-100 rounded-lg px-2.5 py-1.5"
                >
                  <span className="flex-1 min-w-0 break-words text-gray-800" title={p.name}>
                    {p.name}
                    {(draft.flags ?? []).some((f) => f.product === p.name) && (
                      <span className="ml-1" title={t('admin.import.needs_attention')}>
                        ⚠
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-gray-400">
                    {p.extras.length > 0 && `+${p.extras.length} `}
                  </span>
                  <span className="font-medium text-gray-700">
                    {(p.price_cents / 100).toFixed(2)} {draft.currency ?? ''}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </Card>
      )}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
  testId,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
  testId: string
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={
        active
          ? 'rounded-lg bg-indigo-600 text-white px-3 py-1.5 text-sm font-medium'
          : 'rounded-lg bg-white border border-gray-200 text-gray-600 px-3 py-1.5 text-sm hover:bg-gray-50'
      }
    >
      {children}
    </button>
  )
}
